import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import DateInput from '../components/DateInput';
import EtapeTracabilite from '../components/EtapeTracabilite';
import BatchAnalytiquePanel from '../components/BatchAnalytiquePanel';
import CoutRevient from '../components/CoutRevient';
import CourbeCuissonBatch from '../components/CourbeCuissonBatch';
import RecetteStockPanel from '../components/RecetteStockPanel';
import Icon from '../components/Icon';
import './ProductionSaucisses.css';

// ─── CONSTANTES ──────────────────────────────

const ETAPES = [
  { id: 1, label: 'Batch & Recette' },
  { id: 2, label: 'Traçabilité' },
  { id: 3, label: 'Production' },
  { id: 4, label: 'Confirmation' },
];

const EPICES_DEFAUT = {
  kosher_salt_pct  : 1.60,
  curing_salt_pct  : 0.25,
  garlic_pct       : 0.45,
  onion_pct        : 0.45,
  black_pepper_pct : 0.35,
  paprika_pct      : 0.25,
  dry_milk_pct     : 2.50,
};

const EPICES_LABELS = [
  { key: 'kosher_salt_pct',  label: 'Kosher Salt' },
  { key: 'curing_salt_pct',  label: 'Pink Curing Salt #1' },
  { key: 'garlic_pct',       label: 'Granulated Garlic' },
  { key: 'onion_pct',        label: 'Granulated Onion' },
  { key: 'black_pepper_pct', label: 'Black Pepper 16 mesh' },
  { key: 'paprika_pct',      label: 'Paprika fumé' },
  { key: 'dry_milk_pct',     label: 'Dry Milk Powder' },
];

// ─── HELPERS ─────────────────────────────────

function calcEpices(viande_kg, pcts) {
  const base = viande_kg * 1000;
  return {
    kosher_salt_g  : Math.round(base * pcts.kosher_salt_pct  / 100),
    curing_salt_g  : Math.round(base * pcts.curing_salt_pct  / 100),
    garlic_g       : Math.round(base * pcts.garlic_pct       / 100),
    onion_g        : Math.round(base * pcts.onion_pct        / 100),
    black_pepper_g : Math.round(base * pcts.black_pepper_pct / 100),
    paprika_g      : Math.round(base * pcts.paprika_pct      / 100),
    dry_milk_g     : Math.round(base * pcts.dry_milk_pct     / 100),
  };
}

function badgeRatioGras(pct) {
  if (pct >= 30 && pct <= 35) return { cls: 'badge-success', label: `${pct}% OK` };
  if (pct >= 25 && pct < 30)  return { cls: 'badge-warning', label: `${pct}%` };
  if (pct > 35  && pct <= 40) return { cls: 'badge-warning', label: `${pct}%` };
  return { cls: 'badge-danger', label: `${pct}% hors cible` };
}

function fmtG(g) {
  return g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g}g`;
}

// ─── STEPPER ─────────────────────────────────

function Stepper({ etape, nbTracabiliteNonLies = 0, onStepClick, hasBatch, isFinalise }) {
  const peutCliquer = (cible) => {
    if (!hasBatch && cible > 1) return false;
    if (cible < etape) return true;
    if (isFinalise) return true;
    if (cible <= etape) return true;
    return etape >= cible - 1;
  };

  return (
    <div className="stepper">
      {ETAPES.map((e, i) => {
        const clickable = onStepClick && peutCliquer(e.id);
        return (
          <React.Fragment key={e.id}>
            <div
              className={`step ${etape === e.id ? 'active' : etape > e.id ? 'done' : ''} ${clickable ? 'step-cliquable' : ''}`}
              style={{ position: 'relative', cursor: clickable ? 'pointer' : 'default' }}
              onClick={() => clickable && onStepClick(e.id)}
              title={clickable ? `Aller à l'étape ${e.id} — ${e.label}` : ''}
            >
              <div className="step-circle">
                {etape > e.id ? <Icon name="check" size={14} weight="bold" /> : e.id}
              </div>
              {e.id === 2 && nbTracabiliteNonLies > 0 && (
                <span className="step-badge-warning">{nbTracabiliteNonLies} !</span>
              )}
              <span className="step-label">{e.label}</span>
            </div>
            {i < ETAPES.length - 1 && (
              <div className={`step-line ${etape > e.id ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── ÉTAPE 1 — Batch & Recette ───────────────

function EtapeBatch({ onNext, recetteDefaut, prefill }) {
  const [nomAuto, setNomAuto] = useState('');
  const [nomManuel, setNomManuel] = useState(false);

  useEffect(() => {
    if (!prefill?.nom) {
      api.get('/batches/prochain-numero/saucisse')
        .then(r => setNomAuto(r.nom))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const [form, setForm] = useState({
    nom           : prefill?.nom            || '',
    date_prod     : prefill?.date_prod      || new Date().toISOString().split('T')[0],
    operateurs    : prefill?.operateurs     || '',
    fournisseur   : prefill?.fournisseur    || '',
    poids_porc_kg : prefill?.poids_porc_kg  || '',
    poids_boeuf_kg: prefill?.poids_boeuf_kg || '',
    poids_gras_kg : prefill?.poids_gras_kg  || '',
    prix_kg_porc  : prefill?.prix_kg_porc   || '',
    prix_kg_boeuf : prefill?.prix_kg_boeuf  || '',
    prix_kg_gras  : prefill?.prix_kg_gras   || '',
    poids_jalapeno_g: prefill?.poids_jalapeno_g || '',
    poids_fromage_g : prefill?.poids_fromage_g  || '',
    nb_boyaux       : prefill?.nb_boyaux        || '',
    prix_saucisse   : prefill?.prix_saucisse    || String(recetteDefaut?.prix_saucisse || 4.00),
    ...EPICES_DEFAUT,
    ...(recetteDefaut ? {
      kosher_salt_pct  : recetteDefaut.kosher_salt_pct,
      curing_salt_pct  : recetteDefaut.curing_salt_pct,
      garlic_pct       : recetteDefaut.garlic_pct,
      onion_pct        : recetteDefaut.onion_pct,
      black_pepper_pct : recetteDefaut.black_pepper_pct,
      paprika_pct      : recetteDefaut.paprika_pct,
      dry_milk_pct     : recetteDefaut.dry_milk_pct,
    } : {}),
    ...(prefill?.pcts || {}),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const submittingRef = useRef(false);

  const porc_kg   = parseFloat(form.poids_porc_kg)  || 0;
  const boeuf_kg  = parseFloat(form.poids_boeuf_kg) || 0;
  const gras_kg   = parseFloat(form.poids_gras_kg)  || 0;
  const viande_kg = porc_kg + boeuf_kg + gras_kg;
  const ratio_gras = viande_kg > 0 ? Math.round((gras_kg / viande_kg) * 1000) / 10 : 0;
  const badgeGras  = badgeRatioGras(ratio_gras);

  const pcts = {
    kosher_salt_pct  : parseFloat(form.kosher_salt_pct)  || 1.60,
    curing_salt_pct  : parseFloat(form.curing_salt_pct)  || 0.25,
    garlic_pct       : parseFloat(form.garlic_pct)       || 0.45,
    onion_pct        : parseFloat(form.onion_pct)        || 0.45,
    black_pepper_pct : parseFloat(form.black_pepper_pct) || 0.35,
    paprika_pct      : parseFloat(form.paprika_pct)      || 0.25,
    dry_milk_pct     : parseFloat(form.dry_milk_pct)     || 2.50,
  };
  const epices = calcEpices(viande_kg, pcts);
  const total_epices_g = Object.values(epices).reduce((s, v) => s + v, 0);

  const prixSaucisse = parseFloat(form.prix_saucisse) || 4.00;
  const prixPaquet   = Math.round(prixSaucisse * 4 * 100) / 100;

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const resetEpices = () => {
    if (recetteDefaut) {
      setForm(prev => ({
        ...prev,
        kosher_salt_pct  : recetteDefaut.kosher_salt_pct,
        curing_salt_pct  : recetteDefaut.curing_salt_pct,
        garlic_pct       : recetteDefaut.garlic_pct,
        onion_pct        : recetteDefaut.onion_pct,
        black_pepper_pct : recetteDefaut.black_pepper_pct,
        paprika_pct      : recetteDefaut.paprika_pct,
        dry_milk_pct     : recetteDefaut.dry_milk_pct,
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.date_prod) {
      setError('Date requise');
      return;
    }
    if (!form.operateurs) {
      setError('Veuillez sélectionner les opérateurs');
      return;
    }
    // Anti-double-clic
    if (submittingRef.current) return;
    if (loading) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const nomEffectif = form.nom.trim() || nomAuto || undefined;
      const res = await api.post('/saucisses/batches', {
        nom          : nomEffectif,
        date_prod    : form.date_prod,
        operateurs   : form.operateurs,
        fournisseur  : form.fournisseur || null,
        prix_kg_porc : parseFloat(form.prix_kg_porc)   || 0,
        prix_kg_boeuf: parseFloat(form.prix_kg_boeuf)  || 0,
        prix_kg_gras : parseFloat(form.prix_kg_gras)   || 0,
        poids_porc_kg : porc_kg,
        poids_boeuf_kg: boeuf_kg,
        poids_gras_kg : gras_kg,
        poids_jalapeno_g: parseFloat(form.poids_jalapeno_g) || 0,
        poids_fromage_g : parseFloat(form.poids_fromage_g)  || 0,
        nb_boyaux       : parseInt(form.nb_boyaux)           || 0,
        prix_saucisse   : prixSaucisse,
        ...pcts,
      });
      onNext({ batchId: res.id, form, viande_kg, pcts, epices, ratio_gras, prixSaucisse, prixPaquet });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la création du batch');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="etape-content">
      {error && <div className="alert-error">{error}</div>}

      <RecetteStockPanel produit="saucisses" poidsCruKg={viande_kg > 0 ? viande_kg : ''} />

      {/* Batch */}
      <div className="card form-section">
        <h3>Informations du batch</h3>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Numéro du batch</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={form.nom}
                onChange={e => set('nom', e.target.value)}
                readOnly={!nomManuel}
                style={{ flex: 1, opacity: nomManuel ? 1 : 0.75, background: nomManuel ? undefined : 'rgba(255,255,255,0.04)' }}
                placeholder={nomAuto || 'Chargement…'}
              />
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => setNomManuel(m => !m)} style={{ whiteSpace: 'nowrap' }}>
                {nomManuel ? '↩ Auto' : '✎ Modifier'}
              </button>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>Numéro auto-généré</span>
          </div>
          <div className="form-group">
            <label>Date de production *</label>
            <DateInput value={form.date_prod} onChange={v => set('date_prod', v)} />
          </div>
          <div className="form-group">
            <label>Opérateurs *</label>
            <select value={form.operateurs} onChange={e => set('operateurs', e.target.value)} required>
              <option value="">— Sélectionner —</option>
              <option value="LCU">LCU</option>
              <option value="ELA">ELA</option>
              <option value="LCU+ELA">Les deux</option>
            </select>
          </div>
          <div className="form-group">
            <label>Fournisseur</label>
            <input value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} placeholder="ex: Viandes Gosselin" />
          </div>
        </div>
      </div>

      {/* Viandes */}
      <div className="card form-section">
        <div className="section-header-row">
          <h3>Viandes</h3>
          <span className={`badge ${badgeGras.cls}`}>{badgeGras.label}</span>
        </div>
        <table className="esc-table viandes-table">
          <thead>
            <tr>
              <th>Viande</th>
              <th>Poids (kg)</th>
              <th>% du total</th>
              <th>Prix / kg</th>
            </tr>
          </thead>
          <tbody>
            {[
              { key: 'poids_porc_kg',   prixKey: 'prix_kg_porc',  label: 'Porc (épaule)', val: porc_kg  },
              { key: 'poids_boeuf_kg',  prixKey: 'prix_kg_boeuf', label: 'Bœuf',           val: boeuf_kg },
              { key: 'poids_gras_kg',   prixKey: 'prix_kg_gras',  label: 'Gras wagyu',     val: gras_kg  },
            ].map(row => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>
                  <input
                    type="number" min="0" step="0.1"
                    className="input-inline"
                    value={form[row.key]}
                    onChange={e => set(row.key, e.target.value)}
                    placeholder="0.00"
                  />
                </td>
                <td className={row.key === 'poids_gras_kg' ? `ratio-gras ${badgeGras.cls.replace('badge-', 'text-')}` : ''}>
                  {viande_kg > 0 ? `${Math.round(row.val / viande_kg * 1000) / 10}%` : '—'}
                </td>
                <td>
                  <div className="prix-input">
                    <input
                      type="number" min="0" step="0.01"
                      className="input-inline"
                      value={form[row.prixKey]}
                      onChange={e => set(row.prixKey, e.target.value)}
                      placeholder="0.00"
                    />
                    <span>$/kg</span>
                  </div>
                </td>
              </tr>
            ))}
            <tr className="total-row">
              <td><strong>TOTAL</strong></td>
              <td><strong>{viande_kg > 0 ? `${Math.round(viande_kg * 100) / 100} kg` : '—'}</strong></td>
              <td><strong>100%</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Intrants */}
      <div className="card form-section">
        <h3>Intrants</h3>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Jalapeño (g)</label>
            <input type="number" min="0" value={form.poids_jalapeno_g} onChange={e => set('poids_jalapeno_g', e.target.value)} placeholder="500" />
          </div>
          <div className="form-group">
            <label>Fromage cheddar (g)</label>
            <input type="number" min="0" value={form.poids_fromage_g} onChange={e => set('poids_fromage_g', e.target.value)} placeholder="1400" />
          </div>
          <div className="form-group">
            <label>Boyaux (#)</label>
            <input type="number" min="0" value={form.nb_boyaux} onChange={e => set('nb_boyaux', e.target.value)} placeholder="5" />
          </div>
        </div>
      </div>

      {/* Épices */}
      <div className="card form-section">
        <div className="section-header-row">
          <h3>Épices <span className="muted">(% du poids total viande)</span></h3>
          <button className="btn btn-ghost btn-sm" onClick={resetEpices}>Réinitialiser par défaut</button>
        </div>
        <table className="esc-table epices-table">
          <thead>
            <tr>
              <th>Épice</th>
              <th>%</th>
              <th>Quantité calculée</th>
            </tr>
          </thead>
          <tbody>
            {EPICES_LABELS.map(e => {
              const gKey = e.key.replace('_pct', '_g');
              return (
                <tr key={e.key}>
                  <td>{e.label}</td>
                  <td>
                    <div className="pct-input">
                      <input
                        type="number" min="0" step="0.01"
                        className="input-inline"
                        value={form[e.key]}
                        onChange={ev => set(e.key, ev.target.value)}
                      />
                      <span>%</span>
                    </div>
                  </td>
                  <td className={viande_kg > 0 ? 'gold' : 'muted'}>
                    {viande_kg > 0 ? fmtG(epices[gKey] || 0) : '—'}
                  </td>
                </tr>
              );
            })}
            <tr className="total-row">
              <td><strong>TOTAL</strong></td>
              <td></td>
              <td className={viande_kg > 0 ? 'gold' : 'muted'}>
                <strong>{viande_kg > 0 ? fmtG(total_epices_g) : '—'}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Prix */}
      <div className="card form-section">
        <h3>Prix</h3>
        <div className="prix-section">
          <div className="form-group prix-group">
            <label>Prix par saucisse individuelle</label>
            <div className="prix-input">
              <input
                type="number" min="0" step="0.25"
                value={form.prix_saucisse}
                onChange={e => set('prix_saucisse', e.target.value)}
              />
              <span>$</span>
            </div>
          </div>
          <div className="prix-paquet-display">
            <span className="muted">Prix du paquet de 4 :</span>
            <span className="gold prix-paquet-val">{prixPaquet.toFixed(2)} $</span>
          </div>
        </div>
      </div>

      {/* Résumé live */}
      {viande_kg > 0 && (
        <div className="card resume-live">
          <h4>Résumé</h4>
          <div className="resume-grid">
            <div><span className="muted">Total viande</span><strong>{Math.round(viande_kg * 100) / 100} kg</strong></div>
            <div>
              <span className="muted">Ratio gras</span>
              <span className={`badge ${badgeGras.cls}`}>{badgeGras.label}</span>
            </div>
            <div><span className="muted">Total épices</span><strong>{fmtG(total_epices_g)}</strong></div>
            <div><span className="muted">Boyaux</span><strong>{form.nb_boyaux || '—'}</strong></div>
            <div><span className="muted">Prix paquet</span><strong className="gold">{prixPaquet.toFixed(2)} $</strong></div>
            <div><span className="muted">Saucisses estimées</span><strong>{form.nb_boyaux ? parseInt(form.nb_boyaux) * 54 : '—'}</strong></div>
          </div>
        </div>
      )}

      <div className="etape-actions">
        <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !form.operateurs}>
          {loading ? 'Création...' : 'Créer le batch →'}
        </button>
      </div>
    </div>
  );
}

// ─── ÉTAPE 1 BIS — Édition d'un batch existant ─

function EtapeBatchEditerSaucisses({ batchData, onUpdated }) {
  const batch = batchData.form;
  const isFinalise = batch.statut === 'finalise';
  const [form, setForm] = useState({
    date_prod      : batch.date_prod || '',
    operateurs     : batch.operateurs || '',
    fournisseur    : batch.fournisseur || '',
    poids_porc_kg  : batch.poids_porc_kg || '',
    poids_boeuf_kg : batch.poids_boeuf_kg || '',
    poids_gras_kg  : batch.poids_gras_kg || '',
    prix_kg_porc   : batch.prix_kg_porc || '',
    prix_kg_boeuf  : batch.prix_kg_boeuf || '',
    prix_kg_gras   : batch.prix_kg_gras || '',
    poids_jalapeno_g: batch.poids_jalapeno_g || '',
    poids_fromage_g : batch.poids_fromage_g || '',
    nb_boyaux       : batch.nb_boyaux || '',
    prix_saucisse   : batch.prix_saucisse || 4.00,
    notes           : batch.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur]   = useState('');
  const [toast, setToast]     = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const enregistrer = async () => {
    setLoading(true);
    setErreur('');
    setToast('');
    try {
      const updated = await api.put(`/saucisses/batches/${batchData.batchId}`, {
        date_prod      : form.date_prod || null,
        operateurs     : form.operateurs || null,
        fournisseur    : form.fournisseur || null,
        poids_porc_kg  : form.poids_porc_kg !== '' ? parseFloat(form.poids_porc_kg) : null,
        poids_boeuf_kg : form.poids_boeuf_kg !== '' ? parseFloat(form.poids_boeuf_kg) : null,
        poids_gras_kg  : form.poids_gras_kg !== '' ? parseFloat(form.poids_gras_kg) : null,
        prix_kg_porc   : form.prix_kg_porc !== '' ? parseFloat(form.prix_kg_porc) : null,
        prix_kg_boeuf  : form.prix_kg_boeuf !== '' ? parseFloat(form.prix_kg_boeuf) : null,
        prix_kg_gras   : form.prix_kg_gras !== '' ? parseFloat(form.prix_kg_gras) : null,
        poids_jalapeno_g: form.poids_jalapeno_g !== '' ? parseFloat(form.poids_jalapeno_g) : null,
        poids_fromage_g : form.poids_fromage_g !== '' ? parseFloat(form.poids_fromage_g) : null,
        nb_boyaux       : form.nb_boyaux !== '' ? parseInt(form.nb_boyaux) : null,
        prix_saucisse   : form.prix_saucisse !== '' ? parseFloat(form.prix_saucisse) : null,
        notes           : form.notes || null,
      });
      // Mise à jour du batchData (recalcul des champs dérivés)
      const porc_kg  = parseFloat(updated.poids_porc_kg)  || 0;
      const boeuf_kg = parseFloat(updated.poids_boeuf_kg) || 0;
      const gras_kg  = parseFloat(updated.poids_gras_kg)  || 0;
      const viande_kg = porc_kg + boeuf_kg + gras_kg;
      const ratio_gras = viande_kg > 0 ? Math.round((gras_kg / viande_kg) * 1000) / 10 : 0;
      const prixSaucisse = parseFloat(updated.prix_saucisse) || 4.00;
      const prixPaquet   = Math.round(prixSaucisse * 4 * 100) / 100;
      onUpdated({
        ...batchData,
        form: updated,
        viande_kg, ratio_gras, prixSaucisse, prixPaquet,
      });
      setToast('Modifications enregistrées');
      setTimeout(() => setToast(''), 3000);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card form-section">
      <h3>
        Modifier les informations — <span className="gold">{batch.nom}</span>
        {isFinalise && (
          <span className="badge badge-success" style={{ marginLeft: 8, fontSize: 11 }}>Finalisé</span>
        )}
      </h3>
      {erreur && <p className="danger" style={{ marginBottom: 12 }}>{erreur}</p>}
      {toast && <p className="success" style={{ marginBottom: 12 }}>{toast}</p>}

      <div className="form-grid-3">
        <div className="form-group">
          <label>Date de production</label>
          <DateInput value={form.date_prod} onChange={v => set('date_prod', v)} />
        </div>
        <div className="form-group">
          <label>Opérateurs</label>
          <select value={form.operateurs} onChange={e => set('operateurs', e.target.value)}>
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>
        <div className="form-group">
          <label>Fournisseur</label>
          <input value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} placeholder="ex: Viandes Gosselin" />
        </div>
        <div className="form-group">
          <label>Poids porc (kg)</label>
          <input type="number" step="0.1" value={form.poids_porc_kg}
            disabled={isFinalise}
            onChange={e => set('poids_porc_kg', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Poids bœuf (kg)</label>
          <input type="number" step="0.1" value={form.poids_boeuf_kg}
            disabled={isFinalise}
            onChange={e => set('poids_boeuf_kg', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Poids gras (kg)</label>
          <input type="number" step="0.1" value={form.poids_gras_kg}
            disabled={isFinalise}
            onChange={e => set('poids_gras_kg', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Prix porc ($/kg)</label>
          <input type="number" step="0.01" value={form.prix_kg_porc}
            onChange={e => set('prix_kg_porc', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Prix bœuf ($/kg)</label>
          <input type="number" step="0.01" value={form.prix_kg_boeuf}
            onChange={e => set('prix_kg_boeuf', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Prix gras ($/kg)</label>
          <input type="number" step="0.01" value={form.prix_kg_gras}
            onChange={e => set('prix_kg_gras', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Jalapeño (g)</label>
          <input type="number" value={form.poids_jalapeno_g}
            disabled={isFinalise}
            onChange={e => set('poids_jalapeno_g', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Fromage (g)</label>
          <input type="number" value={form.poids_fromage_g}
            disabled={isFinalise}
            onChange={e => set('poids_fromage_g', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Boyaux (#)</label>
          <input type="number" value={form.nb_boyaux}
            disabled={isFinalise}
            onChange={e => set('nb_boyaux', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Prix saucisse ($)</label>
          <input type="number" step="0.25" value={form.prix_saucisse}
            disabled={isFinalise}
            onChange={e => set('prix_saucisse', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea rows="2" value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>

      <div className="etape-actions">
        <button className="btn btn-primary" onClick={enregistrer} disabled={loading}>
          {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
      {isFinalise && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Batch finalisé : poids et prix saucisse verrouillés. Date, opérateurs, fournisseur, prix d'achat et notes restent éditables.
        </p>
      )}
    </div>
  );
}

// ─── PANNEAU TIMER FUMAGE SAUCISSES ──────────
// Utilise PATCH /api/batches/:id/cuisson/demarrer|arreter (CCP HACCP)
// Les saucisses passent au fumoir (température critique). Fallback HH:MM disponible.

function PanneauTimerFumageSaucisses({ batchId, dateProd }) {
  const [batchInfo, setBatchInfo]     = useState(null);
  const [chargement, setChargement]   = useState(false);
  const [erreur, setErreur]           = useState('');
  const [tempsEcoule, setTempsEcoule] = useState('');
  const [heureManuelle, setHeureManuelle] = useState('');

  // Charger heure_debut / heure_fin du batch générique
  useEffect(() => {
    if (!batchId) return;
    api.get(`/batches/${batchId}/tracabilite`)
      .then(d => setBatchInfo({ heure_debut: d.heure_debut, heure_fin: d.heure_fin }))
      .catch(() => {});
  }, [batchId]);

  useEffect(() => {
    if (!batchInfo?.heure_debut || batchInfo?.heure_fin) return;
    const calculer = () => {
      const ms = new Date() - new Date(batchInfo.heure_debut);
      if (ms < 0) return;
      setTempsEcoule(`${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`);
    };
    calculer();
    const iv = setInterval(calculer, 30000);
    return () => clearInterval(iv);
  }, [batchInfo?.heure_debut, batchInfo?.heure_fin]);

  const fmtH = (iso) => iso ? new Date(iso).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
  const duree = (d, f) => {
    if (!d || !f) return '';
    const ms = new Date(f) - new Date(d);
    if (ms < 0) return '';
    return `${Math.floor(ms / 3600000)}h ${String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0')}min`;
  };

  const refresh = () => {
    api.get(`/batches/${batchId}/tracabilite`)
      .then(d => setBatchInfo({ heure_debut: d.heure_debut, heure_fin: d.heure_fin }))
      .catch(() => {});
  };

  const action = async (type, extra) => {
    setChargement(true); setErreur('');
    try {
      await api.patch(`/batches/${batchId}/cuisson/${type}`, extra || {});
      refresh();
    } catch (e) { setErreur(e.response?.data?.error || 'Erreur'); }
    finally { setChargement(false); }
  };

  return (
    <div className="card timer-cuisson-panel" style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 12 }}>Suivi fumage (CCP)</h4>
      {erreur && <p className="danger" style={{ fontSize: 13, marginBottom: 8 }}>{erreur}</p>}
      {!batchInfo?.heure_debut && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => action('demarrer')} disabled={chargement}>
            <Icon name="demarrer" size={15} />{chargement ? ' ...' : ' Démarrer le fumage'}
          </button>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 12 }}>Ou heure manuelle</label>
              <input type="time" value={heureManuelle} onChange={e => setHeureManuelle(e.target.value)} style={{ width: 120 }} />
            </div>
            {heureManuelle && dateProd && (
              <button className="btn btn-ghost btn-sm" disabled={chargement}
                onClick={() => action('demarrer', { heure_debut: new Date(`${dateProd}T${heureManuelle}:00`).toISOString() })}>
                Enregistrer
              </button>
            )}
          </div>
        </div>
      )}
      {batchInfo?.heure_debut && !batchInfo?.heure_fin && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="success" style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="demarrer" size={14} /> Fumage démarré à {fmtH(batchInfo.heure_debut)}
            </span>
            {tempsEcoule && <span className="gold">{tempsEcoule} écoulées</span>}
          </div>
          <button className="btn btn-danger" onClick={() => action('arreter')} disabled={chargement}>
            <Icon name="arreter" size={15} />{chargement ? ' ...' : ' Arrêter le fumage'}
          </button>
        </div>
      )}
      {batchInfo?.heure_debut && batchInfo?.heure_fin && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted">Fumage {fmtH(batchInfo.heure_debut)} → {fmtH(batchInfo.heure_fin)}</span>
          <span className="gold" style={{ fontWeight: 600 }}>Durée : {duree(batchInfo.heure_debut, batchInfo.heure_fin)}</span>
        </div>
      )}
    </div>
  );
}

// ─── ÉTAPE 2 — Production ─────────────────────

function EtapeProduction({ batchData, onNext, onBack }) {
  const [nbSaucisses, setNbSaucisses]   = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [yieldStats, setYieldStats]     = useState(null);
  const [apercuData, setApercuData]     = useState(null);
  const [loadingApercu, setLoadingApercu] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    api.get('/saucisses/yield/stats')
      .then(setYieldStats)
      .catch(() => {});
  }, []);

  // Invalider l'aperçu si l'utilisateur modifie le nombre de saucisses après l'avoir vu
  // → empêche de confirmer sur des chiffres qui ne correspondent plus à la valeur courante
  useEffect(() => {
    if (apercuData !== null) {
      setApercuData(null);
    }
  }, [nbSaucisses]); // eslint-disable-line

  const nb    = parseInt(nbSaucisses) || 0;
  const paquets = Math.floor(nb / 4);
  const restantes = nb % 4;

  const { viande_kg, pcts } = batchData;
  const jalapeno_g  = parseFloat(batchData.form.poids_jalapeno_g) || 0;
  const fromage_g   = parseFloat(batchData.form.poids_fromage_g)  || 0;
  const epices      = calcEpices(viande_kg, pcts);
  const total_epices_g = Object.values(epices).reduce((s, v) => s + v, 0);
  const total_matieres_g = viande_kg * 1000 + jalapeno_g + fromage_g + total_epices_g;

  const poids_moyen_g = nb > 0 ? Math.round((total_matieres_g / nb) * 10) / 10 : 0;
  const poids_paquet_g = Math.round(poids_moyen_g * 4 * 10) / 10;
  const yield_global   = total_matieres_g > 0 && nb > 0
    ? Math.round((nb * poids_moyen_g) / total_matieres_g * 1000) / 10
    : 0;

  const prixPorc  = parseFloat(batchData.form.prix_kg_porc)  || 0;
  const prixBoeuf = parseFloat(batchData.form.prix_kg_boeuf) || 0;
  const prixGras  = parseFloat(batchData.form.prix_kg_gras)  || 0;
  const cout = (batchData.viande_kg ? (
    (parseFloat(batchData.form.poids_porc_kg)  || 0) * prixPorc +
    (parseFloat(batchData.form.poids_boeuf_kg) || 0) * prixBoeuf +
    (parseFloat(batchData.form.poids_gras_kg)  || 0) * prixGras
  ) : 0);
  const revenus = Math.round(paquets * batchData.prixPaquet * 100) / 100;
  const marge   = revenus > 0 ? Math.round((revenus - cout) / revenus * 1000) / 10 : 0;

  const yieldMoyen = yieldStats?.yield_moyen;
  const badgeYield = yieldMoyen && yield_global > 0
    ? (yield_global >= yieldMoyen ? 'badge-success' : 'badge-warning')
    : 'badge-muted';

  const voirApercu = async () => {
    if (nb <= 0) { setError('Entrer le nombre de saucisses'); return; }
    setLoadingApercu(true);
    setError('');
    try {
      const res = await api.get(`/saucisses/batches/${batchData.batchId}/apercu-deduction?nb_saucisses=${nb}`);
      setApercuData(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erreur aperçu');
    } finally {
      setLoadingApercu(false);
    }
  };

  const handleConfirm = async () => {
    if (submittingRef.current) return;
    if (loading) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/saucisses/batches/${batchData.batchId}/production`, {
        nb_saucisses: nb,
      });
      onNext(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la production');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="etape-content">
      {error && <div className="alert-error">{error}</div>}

      {/* Panneau play/stop fumage — CCP HACCP saucisses */}
      <PanneauTimerFumageSaucisses batchId={batchData.batchId} dateProd={batchData.form.date_prod} />

      <div className="card form-section">
        <h3>Production — Batch : {batchData.form.nom}</h3>
        <div className="nb-saucisses-input">
          <label>Nombre de saucisses produites</label>
          <div className="input-grand">
            <input
              type="number" min="0"
              value={nbSaucisses}
              onChange={e => setNbSaucisses(e.target.value)}
              placeholder="270"
              autoFocus
            />
          </div>
        </div>
      </div>

      {nb > 0 && (
        <div className="card calculs-production">
          <h3>Résultats calculés</h3>
          <div className="calculs-grid">
            <div className="calcul-item">
              <span className="calcul-label">Paquets de 4 complets</span>
              <span className="calcul-val gold">{paquets}</span>
            </div>
            <div className="calcul-item">
              <span className="calcul-label">Saucisses restantes</span>
              <span className={`calcul-val ${restantes > 0 ? 'danger' : 'success'}`}>{restantes}</span>
            </div>
            <div className="calcul-item">
              <span className="calcul-label">Poids moyen / saucisse</span>
              <span className="calcul-val">{poids_moyen_g}g</span>
            </div>
            <div className="calcul-item">
              <span className="calcul-label">Poids moyen / paquet</span>
              <span className={`calcul-val ${poids_paquet_g >= 450 && poids_paquet_g <= 550 ? 'success' : 'warning'}`}>
                {poids_paquet_g}g
              </span>
            </div>
            <div className="calcul-item">
              <span className="calcul-label">Yield global</span>
              <span className={`calcul-val`}>
                <span className={`badge ${badgeYield}`}>
                  {yield_global}%
                  {yieldMoyen ? ` (moy. ${yieldMoyen}%)` : ''}
                </span>
              </span>
            </div>
          </div>

          {restantes > 0 && (
            <div className="alert-warning">
              <Icon name="attention" size={14} /> {restantes} saucisse{restantes > 1 ? 's' : ''} non emballée{restantes > 1 ? 's' : ''} — à consommer ou intégrer au prochain batch.
            </div>
          )}

          <div className="resume-financier">
            <h4>Résumé financier</h4>
            <div className="fin-grid">
              <div><span className="muted">Revenus potentiels</span><strong className="gold">{revenus.toFixed(2)} $</strong></div>
              <div><span className="muted">Coût matières estimé</span><strong>{cout > 0 ? `${cout.toFixed(2)} $` : '—'}</strong></div>
              <div><span className="muted">Marge brute</span><strong className={marge > 0 ? 'success' : 'muted'}>{cout > 0 ? `${marge}%` : '—'}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Rentabilité — calcul live (visible dès qu'on a un aperçu) */}
      {apercuData && nb > 0 && (() => {
        const stats = apercuData.stats || {};
        const coutTotal   = apercuData.cout_total_batch != null ? apercuData.cout_total_batch : null;
        const coutParPaq  = (coutTotal != null && paquets > 0)
          ? Math.round(coutTotal / paquets * 100) / 100
          : (stats.cout_par_unite || null);
        const coutParKg   = stats.cout_par_kg || null;
        // Yield = nb_saucisses × poids_moyen / total_matières → ratio constant à 100% si pas de pertes
        // On utilise le yield_global déjà calculé (ratio paquets vs matières)
        const yieldPct    = yield_global > 0 ? yield_global : null;
        const margeBrute  = (revenus > 0 && coutTotal != null)
          ? Math.round((revenus - coutTotal) / revenus * 1000) / 10
          : null;
        return (
          <div className="card" style={{
            marginTop: 16,
            padding: 14,
            background: 'rgba(200,148,26,0.06)',
            border: '1px solid rgba(200,148,26,0.25)',
          }}>
            <h4 style={{ marginBottom: 12 }}>Rentabilité — calcul live</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
            }}>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Coût matières / paquet</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {coutParPaq != null ? `${coutParPaq.toFixed(2)} $` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Coût matières / kg viande</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {coutParKg != null ? `${coutParKg.toFixed(2)} $/kg` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Yield (matières utilisées)</div>
                <div className={
                  yieldPct == null ? 'muted'
                  : yieldPct >= 90 ? 'success'
                  : yieldPct >= 75 ? 'warning'
                  : 'danger'
                } style={{ fontSize: 20, fontWeight: 700 }}>
                  {yieldPct != null ? `${yieldPct.toFixed(1)} %` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Revenu estimé</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>
                  {revenus > 0 ? `${revenus.toFixed(2)} $` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Marge brute</div>
                <div className={
                  margeBrute == null ? 'muted'
                  : margeBrute >= 50 ? 'success'
                  : margeBrute >= 30 ? 'warning'
                  : 'danger'
                } style={{ fontSize: 20, fontWeight: 700 }}>
                  {margeBrute != null ? `${margeBrute.toFixed(1)} %` : '—'}
                </div>
              </div>
              <div>
                <div className="muted" style={{ fontSize: 11 }}>Paquets produits</div>
                <div className="gold" style={{ fontSize: 20, fontWeight: 700 }}>{paquets}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Aperçu déduction inventaire */}
      {apercuData && apercuData.deductions && apercuData.deductions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 12 }}>Aperçu — Déduction inventaire</h4>
          <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Ces ingrédients seront déduits de l'inventaire lors de la finalisation :
          </p>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Ingrédient</th>
                <th>Déduit</th>
                <th>Stock après</th>
              </tr>
            </thead>
            <tbody>
              {apercuData.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td>
                    <span className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                      {d.statut === 'negatif' && <Icon name="erreur" size={13} style={{ marginLeft: 4 }} />}
                      {d.statut === 'bas' && <Icon name="attention" size={13} style={{ marginLeft: 4 }} />}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {apercuData.deductions.some(d => d.statut !== 'ok') && (
            <div className="alert-warning" style={{ marginTop: 10 }}>
              <Icon name="attention" size={14} /> {apercuData.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) passeront sous le seuil ou seront à 0.
            </div>
          )}
        </div>
      )}

      <CourbeCuissonBatch batchId={batchData.batchId} produit="saucisse" sessionActive={true} />

      <div className="etape-actions">
        <button className="btn btn-ghost" onClick={onBack}>← Retour</button>
        {!apercuData ? (
          <button className="btn btn-primary" onClick={voirApercu} disabled={loadingApercu || nb <= 0}>
            {loadingApercu ? 'Calcul...' : 'Aperçu →'}
          </button>
        ) : (
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setApercuData(null)}>
              Modifier
            </button>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Traitement...' : 'Confirmer et finaliser →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ÉTAPE 3 — Confirmation ───────────────────

function EtapeConfirmation({ batchData, resultData }) {
  const wc = resultData.wc_sync;

  const handlePrint = () => window.print();

  return (
    <div className="etape-content">
      <div className="success-banner">
        <div className="success-icon">
          <Icon name="succes" size={22} />
        </div>
        <h2>Batch finalisé avec succès !</h2>
      </div>

      <div className="card fiche-batch printable">
        <div className="fiche-header">
          <div className="fiche-logo">ESCAMEL</div>
          <div className="fiche-title">FICHE DE PRODUCTION</div>
        </div>
        <div className="fiche-grid">
          <div><span className="muted">Batch</span><strong>{batchData.form.nom}</strong></div>
          <div><span className="muted">Date</span><strong>{batchData.form.date_prod}</strong></div>
          <div><span className="muted">Produit</span><strong>Saucisses Jalapeño Cheddar</strong></div>
          <div><span className="muted">Saucisses produites</span><strong className="gold">{resultData.nb_saucisses}</strong></div>
          <div><span className="muted">Paquets de 4</span><strong className="gold">{resultData.nb_paquets}</strong></div>
          {resultData.saucisses_restantes > 0 && (
            <div><span className="muted">Saucisses restantes</span><strong className="danger">{resultData.saucisses_restantes}</strong></div>
          )}
          <div><span className="muted">Yield global</span><strong>{resultData.yield_global}%</strong></div>
          <div><span className="muted">Ratio gras</span><strong>{batchData.ratio_gras}%</strong></div>
          <div><span className="muted">Poids moyen paquet</span><strong>{resultData.poids_moyen_paquet_g}g</strong></div>
          <div><span className="muted">Prix paquet</span><strong className="gold">{resultData.prix_paquet.toFixed(2)} $</strong></div>
          <div><span className="muted">Revenus potentiels</span><strong className="gold">{resultData.revenus_potentiels.toFixed(2)} $</strong></div>
        </div>
      </div>

      <div className="card wc-status">
        <h3>WooCommerce — SAUJC4</h3>
        {wc?.success ? (
          <div className="wc-success-grid">
            <div><span className="muted">Stock avant</span><strong>{wc.stock_avant}</strong></div>
            <div><span className="muted">Ajouté</span><strong className="gold">+{resultData.nb_paquets}</strong></div>
            <div><span className="muted">Stock après</span><strong className="success">{wc.stock_apres}</strong></div>
          </div>
        ) : wc?.errors?.length > 0 ? (
          <div className="alert-error">Erreur WooCommerce : {wc.errors[0]}</div>
        ) : (
          <p className="muted">WooCommerce non configuré — stock non mis à jour.</p>
        )}
      </div>

      {resultData.ecriture_id && (
        <div className="alert-info">
          Écriture comptable créée (#{resultData.ecriture_id})
        </div>
      )}

      {resultData.deductions && resultData.deductions.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Inventaire déduit</h3>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>Déduit</th><th>Stock restant</th></tr>
            </thead>
            <tbody>
              {resultData.deductions.map(d => (
                <tr key={d.matiere_id}>
                  <td>{d.nom}</td>
                  <td className="gold">−{d.quantite_deduite} {d.unite}</td>
                  <td>
                    <span className={d.statut === 'negatif' ? 'danger' : d.statut === 'bas' ? 'warning' : 'success'}>
                      {d.stock_apres} {d.unite}
                      {d.statut === 'negatif' && <Icon name="erreur" size={13} style={{ marginLeft: 4 }} />}
                      {d.statut === 'bas' && <> <Icon name="attention" size={13} /> Bas</>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {resultData.deductions.some(d => d.statut !== 'ok') && (
            <p className="warning" style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="attention" size={14} /> {resultData.deductions.filter(d => d.statut !== 'ok').length} ingrédient(s) à réapprovisionner
            </p>
          )}
        </div>
      )}

      <CoutRevient analytique={resultData.analytique} revenus={resultData.revenus_potentiels} />

      {batchData.batchId && <CourbeCuissonBatch batchId={batchData.batchId} produit="saucisse" sessionActive={false} />}

      <div className="etape-actions">
        <button className="btn btn-ghost" onClick={handlePrint}>Imprimer la fiche</button>
        <a href="/production/saucisses" className="btn btn-primary" onClick={() => window.location.href = '/production/saucisses'}>
          Nouveau batch
        </a>
      </div>
    </div>
  );
}

// ─── VUE RÉSUMÉ BATCH FINALISÉ ───────────────

function VueSommaireFinalisé({ batch, onRetour }) {
  const [ongletVue, setOngletVue] = useState('detail');
  const pcts = {
    kosher_salt_pct  : batch.kosher_salt_pct  || 1.60,
    curing_salt_pct  : batch.curing_salt_pct  || 0.25,
    garlic_pct       : batch.garlic_pct       || 0.45,
    onion_pct        : batch.onion_pct        || 0.45,
    black_pepper_pct : batch.black_pepper_pct || 0.35,
    paprika_pct      : batch.paprika_pct      || 0.25,
    dry_milk_pct     : batch.dry_milk_pct     || 2.50,
  };
  const viande_kg  = (parseFloat(batch.poids_porc_kg) || 0)
                   + (parseFloat(batch.poids_boeuf_kg) || 0)
                   + (parseFloat(batch.poids_gras_kg)  || 0);
  const epices     = calcEpices(viande_kg, pcts);
  const ratio_gras = viande_kg > 0
    ? Math.round(((parseFloat(batch.poids_gras_kg) || 0) / viande_kg) * 1000) / 10 : 0;
  const prix_paquet = (parseFloat(batch.prix_saucisse) || 4) * 4;
  const revenus     = (batch.nb_paquets || 0) * prix_paquet;

  return (
    <div className="etape-content">
      {/* Onglets Détail / Analytique */}
      <div className="onglets" style={{ marginBottom: '1rem' }}>
        <button className={`onglet-btn ${ongletVue === 'detail' ? 'active' : ''}`} onClick={() => setOngletVue('detail')}>
          Détail du batch
        </button>
        <button className={`onglet-btn ${ongletVue === 'analytique' ? 'active' : ''}`} onClick={() => setOngletVue('analytique')}>
          <Icon name="analytics" size={14} /> Analytique
        </button>
      </div>

      {ongletVue === 'analytique' && (
        <BatchAnalytiquePanel batch={batch} />
      )}

      {ongletVue === 'detail' && (
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>Résumé — {batch.nom}</h3>
          <span className="badge badge-success"><Icon name="check" size={12} weight="bold" /> Finalisé</span>
        </div>

        <div className="fiche-grid">
          <div><span className="muted">Date</span><strong>{batch.date_prod}</strong></div>
          <div><span className="muted">Fournisseur</span><strong>{batch.fournisseur || '—'}</strong></div>
          <div><span className="muted">Viande totale</span><strong>{viande_kg > 0 ? `${Math.round(viande_kg * 100) / 100} kg` : '—'}</strong></div>
          <div>
            <span className="muted">Ratio gras</span>
            <span className={`badge ${badgeRatioGras(ratio_gras).cls}`}>{badgeRatioGras(ratio_gras).label}</span>
          </div>
          <div><span className="muted">Saucisses produites</span><strong className="gold">{batch.nb_saucisses || '—'}</strong></div>
          <div><span className="muted">Paquets de 4</span><strong className="gold">{batch.nb_paquets || '—'}</strong></div>
          <div><span className="muted">Yield global</span><strong>{batch.yield_global > 0 ? `${batch.yield_global}%` : '—'}</strong></div>
          <div><span className="muted">Prix paquet</span><strong className="gold">{prix_paquet.toFixed(2)} $</strong></div>
          <div><span className="muted">Revenus générés</span><strong className="gold">{revenus.toFixed(2)} $</strong></div>
        </div>

        <h4 style={{ marginTop: 20, marginBottom: 12 }}>Recette utilisée</h4>
        <table className="esc-table epices-table">
          <thead>
            <tr><th>Épice</th><th>%</th><th>Quantité</th></tr>
          </thead>
          <tbody>
            {EPICES_LABELS.map(e => {
              const gKey = e.key.replace('_pct', '_g');
              return (
                <tr key={e.key}>
                  <td>{e.label}</td>
                  <td>{pcts[e.key]}%</td>
                  <td className={viande_kg > 0 ? 'gold' : 'muted'}>
                    {viande_kg > 0 ? fmtG(epices[gKey] || 0) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="etape-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onRetour}>← Retour à la liste</button>
        </div>
      </div>
      )}

      {/* Bouton retour toujours visible */}
      {ongletVue === 'analytique' && (
        <div style={{ marginTop: '1rem' }}>
          <button className="btn btn-ghost" onClick={onRetour}>← Retour à la liste</button>
        </div>
      )}
    </div>
  );
}

// ─── ONGLET PRODUCTION (stepper) ─────────────

function OngletProduction({ recetteDefaut, prefill, onClearPrefill, batchReprise, onClearBatchReprise }) {
  const [etape, setEtape]                       = useState(1);
  const [batchData, setBatchData]               = useState(null);
  const [resultData, setResultData]             = useState(null);
  const [nbTracabiliteNonLies, setNbTracabiliteNonLies] = useState(0);

  useEffect(() => {
    if (prefill) setEtape(1);
  }, [prefill]);

  // Reprendre un batch en_cours depuis la liste
  useEffect(() => {
    if (!batchReprise) return;
    const b = batchReprise;
    const pcts = {
      kosher_salt_pct  : b.kosher_salt_pct  || 1.60,
      curing_salt_pct  : b.curing_salt_pct  || 0.25,
      garlic_pct       : b.garlic_pct       || 0.45,
      onion_pct        : b.onion_pct        || 0.45,
      black_pepper_pct : b.black_pepper_pct || 0.35,
      paprika_pct      : b.paprika_pct      || 0.25,
      dry_milk_pct     : b.dry_milk_pct     || 2.50,
    };
    const porc_kg   = parseFloat(b.poids_porc_kg)  || 0;
    const boeuf_kg  = parseFloat(b.poids_boeuf_kg) || 0;
    const gras_kg   = parseFloat(b.poids_gras_kg)  || 0;
    const viande_kg = porc_kg + boeuf_kg + gras_kg;
    const epices    = calcEpices(viande_kg, pcts);
    const ratio_gras   = viande_kg > 0 ? Math.round((gras_kg / viande_kg) * 1000) / 10 : 0;
    const prixSaucisse = parseFloat(b.prix_saucisse) || 4.00;
    const prixPaquet   = Math.round(prixSaucisse * 4 * 100) / 100;
    setBatchData({ batchId: b.id, form: b, viande_kg, pcts, epices, ratio_gras, prixSaucisse, prixPaquet });
    setResultData(null);
    setEtape(3);
    if (onClearBatchReprise) onClearBatchReprise();
  }, [batchReprise]); // eslint-disable-line

  const handleStepClick = (cible) => {
    // L'étape 1 avec un batch ouvert affiche EtapeBatchEditerSaucisses.
    // Ne pas réinitialiser — utiliser le bouton dédié pour créer un nouveau batch.
    if (!batchData && cible > 1) return;
    if (cible < etape) { setEtape(cible); return; }
    if (cible <= etape + 1) setEtape(cible);
  };

  return (
    <div>
      {/* Bouton « + Nouveau batch » distinct — visible dès qu'un batch est ouvert */}
      {batchData && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setBatchData(null); setResultData(null); setEtape(1); }}
          >
            + Nouveau batch
          </button>
        </div>
      )}
      <Stepper
        etape={etape}
        nbTracabiliteNonLies={nbTracabiliteNonLies}
        onStepClick={handleStepClick}
        hasBatch={!!batchData}
        isFinalise={false}
      />

      {etape === 1 && (batchData
        ? <EtapeBatchEditerSaucisses batchData={batchData} onUpdated={(d) => setBatchData(d)} />
        : <EtapeBatch
            recetteDefaut={recetteDefaut}
            prefill={prefill}
            onNext={(data) => {
              setBatchData(data);
              setEtape(2);
              if (onClearPrefill) onClearPrefill();
            }}
          />
      )}
      {etape === 2 && batchData && (
        <EtapeTracabilite
          batchId={batchData.batchId}
          batchNom={batchData.form?.nom}
          batchStatut={batchData.form?.statut}
          onNext={() => setEtape(3)}
          onBack={() => setEtape(1)}
          onNbNonLiesChange={setNbTracabiliteNonLies}
        />
      )}
      {etape === 3 && batchData && (
        <EtapeProduction
          batchData={batchData}
          onNext={(res) => { setResultData(res); setEtape(4); }}
          onBack={() => setEtape(2)}
        />
      )}
      {etape === 4 && batchData && resultData && (
        <EtapeConfirmation batchData={batchData} resultData={resultData} />
      )}
    </div>
  );
}

// ─── ONGLET PLANIFICATEUR ─────────────────────

function OngletPlanificateur({ recetteDefaut, onStartBatch }) {
  const [nb, setNb]           = useState('');
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleCalc = async () => {
    if (!parseInt(nb) || parseInt(nb) <= 0) { setError('Entrer un nombre valide'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/saucisses/calculateur?nb_saucisses=${parseInt(nb)}`);
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erreur de calcul');
    } finally {
      setLoading(false);
    }
  };

  const copyListe = () => {
    if (!result) return;
    const v = result.viandes;
    const i = result.intrants;
    const e = result.epices;
    const txt = [
      `LISTE D'ACHATS — ${result.nb_saucisses_cible} saucisses`,
      ``,
      `VIANDES`,
      `  Porc (épaule)   : ${v.porc_kg} kg`,
      `  Bœuf            : ${v.boeuf_kg} kg`,
      `  Gras wagyu      : ${v.gras_kg} kg`,
      `  TOTAL viande    : ${v.total_kg} kg  (ratio gras: ${v.ratio_gras_pct}%)`,
      ``,
      `INTRANTS`,
      `  Jalapeño        : ${i.jalapeno_g}g`,
      `  Fromage cheddar : ${i.fromage_g}g`,
      `  Boyaux          : ${i.nb_boyaux}`,
      ``,
      `ÉPICES`,
      `  Kosher Salt     : ${e.kosher_salt_g}g`,
      `  Curing Salt #1  : ${e.curing_salt_g}g`,
      `  Garlic          : ${e.garlic_g}g`,
      `  Onion           : ${e.onion_g}g`,
      `  Black Pepper    : ${e.black_pepper_g}g`,
      `  Paprika fumé    : ${e.paprika_g}g`,
      `  Dry Milk Powder : ${e.dry_milk_g}g`,
      `  TOTAL épices    : ${e.total_g}g`,
      ``,
      `PRODUCTION ESTIMÉE`,
      `  Saucisses       : ${result.nb_saucisses_cible}`,
      `  Paquets de 4    : ${result.nb_paquets_estimes}`,
      `  Restantes       : ${result.nb_saucisses_cible % 4}`,
    ].join('\n');
    navigator.clipboard.writeText(txt).catch(() => {});
  };

  const confBadge = result
    ? result.confiance === 'élevée' ? 'badge-success'
      : result.confiance === 'moyenne' ? 'badge-warning'
      : 'badge-danger'
    : '';

  return (
    <div className="planificateur">
      <div className="card form-section">
        <h3>Planificateur de production</h3>
        <div className="planif-input-row">
          <span>Je veux produire</span>
          <input
            type="number" min="1"
            value={nb}
            onChange={e => setNb(e.target.value)}
            placeholder="270"
            className="input-nb-saucisses"
            onKeyDown={e => e.key === 'Enter' && handleCalc()}
          />
          <span>saucisses</span>
          <button className="btn btn-primary" onClick={handleCalc} disabled={loading}>
            {loading ? 'Calcul...' : 'Calculer'}
          </button>
        </div>
        {error && <div className="alert-error" style={{ marginTop: 12 }}>{error}</div>}
      </div>

      {result && (
        <div className="planif-result">
          <div className="planif-confidence">
            <span className={`badge ${confBadge}`}>
              Confiance : {result.confiance}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              {result.nb_batches_historique} batch{result.nb_batches_historique !== 1 ? 'es' : ''} historique{result.nb_batches_historique < 3 ? ' — données insuffisantes' : result.nb_batches_historique <= 6 ? ' — estimation approximative' : ' — estimation fiable'}
            </span>
          </div>

          <div className="planif-sections">
            {/* Viandes */}
            <div className="card planif-card">
              <h4>Viandes</h4>
              <div className="planif-list">
                <div><span>Porc (épaule)</span><strong>{result.viandes.porc_kg} kg</strong></div>
                <div><span>Bœuf</span><strong>{result.viandes.boeuf_kg} kg</strong></div>
                <div><span>Gras wagyu</span><strong>{result.viandes.gras_kg} kg</strong></div>
                <div className="total-line">
                  <span>TOTAL viande</span>
                  <strong>{result.viandes.total_kg} kg</strong>
                  <span className={`badge ${badgeRatioGras(result.viandes.ratio_gras_pct).cls}`}>
                    {result.viandes.ratio_gras_pct}%
                  </span>
                </div>
              </div>
            </div>

            {/* Intrants */}
            <div className="card planif-card">
              <h4>Intrants</h4>
              <div className="planif-list">
                <div><span>Jalapeño</span><strong>{result.intrants.jalapeno_g}g</strong></div>
                <div><span>Fromage cheddar</span><strong>{result.intrants.fromage_g >= 1000 ? `${(result.intrants.fromage_g / 1000).toFixed(1)} kg` : `${result.intrants.fromage_g}g`}</strong></div>
                <div><span>Boyaux</span><strong>{result.intrants.nb_boyaux}</strong></div>
              </div>
            </div>

            {/* Épices */}
            <div className="card planif-card">
              <h4>Épices</h4>
              <div className="planif-list">
                <div><span>Kosher Salt</span><strong>{fmtG(result.epices.kosher_salt_g)}</strong></div>
                <div><span>Pink Curing Salt #1</span><strong>{fmtG(result.epices.curing_salt_g)}</strong></div>
                <div><span>Granulated Garlic</span><strong>{fmtG(result.epices.garlic_g)}</strong></div>
                <div><span>Granulated Onion</span><strong>{fmtG(result.epices.onion_g)}</strong></div>
                <div><span>Black Pepper</span><strong>{fmtG(result.epices.black_pepper_g)}</strong></div>
                <div><span>Paprika fumé</span><strong>{fmtG(result.epices.paprika_g)}</strong></div>
                <div><span>Dry Milk Powder</span><strong>{fmtG(result.epices.dry_milk_g)}</strong></div>
                <div className="total-line"><span>TOTAL épices</span><strong>{fmtG(result.epices.total_g)}</strong></div>
              </div>
            </div>

            {/* Production estimée */}
            <div className="card planif-card">
              <h4>Production estimée</h4>
              <div className="planif-list">
                <div><span>Saucisses</span><strong className="gold">{result.nb_saucisses_cible}</strong></div>
                <div><span>Paquets de 4</span><strong className="gold">{result.nb_paquets_estimes}</strong></div>
                <div><span>Restantes</span><strong>{result.nb_saucisses_cible % 4}</strong></div>
                <div><span>Yield utilisé</span><strong>{result.yield_utilise}%</strong></div>
              </div>
            </div>
          </div>

          <div className="planif-actions">
            <button className="btn btn-ghost" onClick={copyListe}>Copier la liste d'achats</button>
            <button className="btn btn-ghost" onClick={() => window.print()}>Imprimer</button>
            <button
              className="btn btn-primary"
              onClick={() => onStartBatch({
                nom             : `Saucisses ${new Date().toLocaleDateString('fr-CA')}`,
                date_prod       : new Date().toISOString().split('T')[0],
                poids_porc_kg   : result.viandes.porc_kg,
                poids_boeuf_kg  : result.viandes.boeuf_kg,
                poids_gras_kg   : result.viandes.gras_kg,
                poids_jalapeno_g: result.intrants.jalapeno_g,
                poids_fromage_g : result.intrants.fromage_g,
                nb_boyaux       : result.intrants.nb_boyaux,
              })}
            >
              Démarrer ce batch →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SECTION LISTE BATCHES ────────────────────

function SectionListeBatches({ onOuvrir }) {
  const [batches, setBatches]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [batchASupprimer, setBatchASupprimer] = useState(null);
  const [suppLoading, setSuppLoading] = useState(false);
  const [toast, setToast]             = useState('');
  const [erreur, setErreur]           = useState('');

  const charger = useCallback(() => {
    api.get('/saucisses/batches')
      .then(setBatches)
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const afficherToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const supprimerBatch = async () => {
    if (!batchASupprimer) return;
    setSuppLoading(true);
    try {
      await api.delete(`/saucisses/batches/${batchASupprimer.id}`);
      setBatches(prev => prev.filter(b => b.id !== batchASupprimer.id));
      afficherToast(`Batch ${batchASupprimer.nom} supprimé`);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setSuppLoading(false);
      setBatchASupprimer(null);
    }
  };

  return (
    <div className="card" style={{ marginTop: 24 }}>
      {batchASupprimer && (
        <ConfirmDeleteModal
          nomBatch={batchASupprimer.nom}
          loading={suppLoading}
          onConfirm={supprimerBatch}
          onAnnuler={() => setBatchASupprimer(null)}
        />
      )}
      {toast && <div className="toast-success">{toast}</div>}

      <h3>Tous les batches saucisses</h3>
      {erreur && <p className="danger" style={{ marginBottom: 8 }}>{erreur}</p>}

      {loading ? (
        <p className="muted">Chargement...</p>
      ) : batches.length === 0 ? (
        <p className="muted" style={{ padding: '12px 0' }}>Aucun batch saucisse enregistré.</p>
      ) : (
        <table className="esc-table">
          <thead>
            <tr>
              <th>Batch</th>
              <th>Date</th>
              <th>Fournisseur</th>
              <th>Viande totale</th>
              <th>Paquets produits</th>
              <th>Dispo</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.id}>
                <td style={{ fontWeight: 600 }}>{b.nom}</td>
                <td>{b.date_prod}</td>
                <td>{b.fournisseur || <span className="muted">—</span>}</td>
                <td>{b.viande_total_kg > 0 ? `${b.viande_total_kg} kg` : <span className="muted">—</span>}</td>
                <td>{b.nb_paquets_produits ?? <span className="muted">—</span>}</td>
                <td><strong className="gold">{b.nb_paquets_dispo ?? 0}</strong></td>
                <td>
                  <span className={`badge ${b.statut === 'finalise' ? 'badge-success' : 'badge-warning'}`}>
                    {b.statut === 'finalise' ? 'Finalisé' : 'En cours'}
                  </span>
                  {b.nb_tracabilite_manquante > 0 && (
                    <span className="badge badge--warning" style={{ marginLeft: 6 }}>
                      <Icon name="attention" size={11} /> {b.nb_tracabilite_manquante} sans facture
                    </span>
                  )}
                </td>
                <td className="td-actions">
                  {onOuvrir && (
                    <button className="btn btn-ghost btn-sm" onClick={() => onOuvrir(b)}>
                      Ouvrir →
                    </button>
                  )}
                  {b.statut === 'finalise' && (
                    <Link className="btn btn-ghost btn-sm" to={`/batches/${b.id}/tracabilite`}>
                      Fiche de traçabilité
                    </Link>
                  )}
                  <button
                    className="btn btn-danger btn-sm btn-icon"
                    onClick={() => setBatchASupprimer(b)}
                    title="Supprimer ce batch"
                  >
                    <Icon name="supprimer" size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── SECTION FIFO ─────────────────────────────

function SectionFIFO() {
  const [stock, setStock]     = useState([]);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(() => {
    api.get('/saucisses/stock')
      .then(setStock)
      .catch(() => setStock([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const avecStock = stock.filter(b => b.nb_paquets_dispo > 0);

  return (
    <div className="card fifo-section">
      <div className="fifo-header">
        <h3>Traçabilité FIFO — Quel batch utiliser en premier</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Épuiser toujours le stock le plus ancien en premier.
        </p>
      </div>

      {loading ? (
        <p className="muted">Chargement...</p>
      ) : avecStock.length === 0 ? (
        <p className="muted" style={{ padding: '16px 0' }}>Aucun stock SAUJC4 disponible.</p>
      ) : (
        <table className="esc-table">
          <thead>
            <tr>
              <th>Priorité</th>
              <th>Batch</th>
              <th>Date production</th>
              <th>Paquets disponibles</th>
              <th>Prix paquet</th>
              <th>Ratio gras</th>
            </tr>
          </thead>
          <tbody>
            {avecStock.map((b) => (
              <tr key={b.batch_id}>
                <td>
                  {b.fifo_priorite === 1
                    ? <span className="badge badge-premier">ÉPUISER EN PREMIER</span>
                    : <span className="badge badge-suivant">{b.fifo_priorite}</span>}
                </td>
                <td style={{ fontWeight: 600 }}>{b.batch_nom}</td>
                <td>{b.date_prod}</td>
                <td><strong className="gold">{b.nb_paquets_dispo}</strong></td>
                <td>{b.prix_paquet.toFixed(2)} $</td>
                <td>
                  <span className={`badge ${badgeRatioGras(b.ratio_gras_pct).cls}`}>{b.ratio_gras_pct}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────

export default function ProductionSaucisses() {
  const [onglet, setOnglet]               = useState('production');
  const [recetteDefaut, setRecetteDefaut] = useState(null);
  const [prefill, setPrefill]             = useState(null);
  const [batchOuvert, setBatchOuvert]     = useState(null); // résumé lecture seule
  const [batchReprise, setBatchReprise]   = useState(null); // reprise stepper

  useEffect(() => {
    api.get('/saucisses/recette')
      .then(setRecetteDefaut)
      .catch(() => {});
  }, []);

  const handleStartBatch = (data) => {
    setPrefill(data);
    setOnglet('production');
  };

  const handleOuvrir = (b) => {
    if (b.statut === 'finalise') {
      setBatchOuvert(b);
    } else {
      setBatchReprise(b);
      setOnglet('production');
    }
  };

  return (
    <div className="production-saucisses">
      <div className="page-header">
        <div>
          <h1 className="page-title">Production — Saucisses Jalapeño Cheddar</h1>
          <p className="muted page-subtitle">Gestion des batches et production par format</p>
        </div>
        <div className="header-actions">
          <Link to="/saucisses/recette" className="btn btn-ghost btn-sm">Modifier la recette de base</Link>
        </div>
      </div>

      {/* Vue résumé batch finalisé */}
      {batchOuvert ? (
        <VueSommaireFinalisé
          batch={batchOuvert}
          onRetour={() => setBatchOuvert(null)}
        />
      ) : (
        <>
          {/* Onglets */}
          <div className="onglets">
            <button
              className={`onglet-btn ${onglet === 'production' ? 'active' : ''}`}
              onClick={() => setOnglet('production')}
            >
              Production
            </button>
            <button
              className={`onglet-btn ${onglet === 'planificateur' ? 'active' : ''}`}
              onClick={() => setOnglet('planificateur')}
            >
              Planificateur
            </button>
          </div>

          {onglet === 'production' && (
            <OngletProduction
              recetteDefaut={recetteDefaut}
              prefill={prefill}
              onClearPrefill={() => setPrefill(null)}
              batchReprise={batchReprise}
              onClearBatchReprise={() => setBatchReprise(null)}
            />
          )}
          {onglet === 'planificateur' && (
            <OngletPlanificateur
              recetteDefaut={recetteDefaut}
              onStartBatch={handleStartBatch}
            />
          )}
        </>
      )}

      <SectionListeBatches onOuvrir={handleOuvrir} />
      <SectionFIFO />
    </div>
  );
}
